'use client';

import React, { createContext, type FC, type ReactNode, useCallback, useMemo, useState } from 'react';

import { between } from '@/utils/between';

export interface CalculatorConfig {
    [key: string]: {
        min: number;
        defaultValue: number;
        max: number;
        round: number;
        step: number;
        unit: string;
    };
}

export interface CalculatorFields {
    [key: string]: number;
}

export interface CalculatorContextFields {
    config: CalculatorConfig;
    fields: CalculatorFields;
    updateConfig: (config: CalculatorConfig) => void;
    updateField: (key: string, value: number | string) => void;
    updateFieldWithStep: (key: string, power: number) => void;
}

interface CalculatorProps {
    children: ReactNode;
}

export const CalculatorContext = createContext<CalculatorContextFields>({
    config: {},
    fields: {},
    updateConfig: () => {},
    updateField: () => {},
    updateFieldWithStep: () => {},
});

export const Calculator: FC<CalculatorProps> = ({ children }) => {
    const [config, setConfig] = useState<CalculatorConfig>({});
    const [fields, setFields] = useState<CalculatorFields>({});

    const updateConfig = useCallback<CalculatorContextFields['updateConfig']>((values) => {
        setConfig(values);

        const newFields: CalculatorFields = {};

        for (const key in values) {
            const fieldConfig = values[key];

            newFields[key] = between({ ...fieldConfig, value: fieldConfig.defaultValue, step: 0 });
        }

        setFields(newFields);
    }, []);

    const updateField = useCallback<CalculatorContextFields['updateField']>((key, value) => {
        setConfig((currentConfig) => {
            const fieldConfig = currentConfig[key];

            if (fieldConfig) {
                setFields((prev) => ({
                    ...prev,
                    [key]: between({ ...fieldConfig, value, step: 0 }),
                }));
            }

            return currentConfig;
        });
    }, []);

    const updateFieldWithStep = useCallback<CalculatorContextFields['updateFieldWithStep']>((key, power) => {
        setConfig((currentConfig) => {
            const fieldConfig = currentConfig[key];

            if (fieldConfig) {
                setFields((prev) => ({
                    ...prev,
                    [key]: between({ ...fieldConfig, value: prev[key], step: fieldConfig.step * power }),
                }));
            }

            return currentConfig;
        });
    }, []);

    const value = useMemo(() => {
        return {
            config,
            fields,
            updateConfig,
            updateField,
            updateFieldWithStep,
        };
    }, [config, fields, updateConfig, updateField, updateFieldWithStep]);

    return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
};
